import React, { useEffect, useRef } from "react";
import { Shield, Smartphone, Brain, UserCheck, ArrowRight, Heart, Eye, Zap, Users, Clock, CheckCircle, ChevronDown, Sparkles, Activity } from "lucide-react";

const IMAGES = {
  hero: "https://static.prod-images.emergentagent.com/jobs/140b3304-82f5-413a-a4b6-594d9907228e/images/55feece4ae245b16f32d1b405a7f7b5a080f5827d1d6af7d4ba39e7aa38e6020.png",
  scan: "https://static.prod-images.emergentagent.com/jobs/140b3304-82f5-413a-a4b6-594d9907228e/images/0d967517f3ebe035ce09bd78c2a076954d10c243e1399064add2c69dbcb10216.png",
  esthetician: "https://static.prod-images.emergentagent.com/jobs/140b3304-82f5-413a-a4b6-594d9907228e/images/9d154c202698165fab83c6c0d54ee5f35bf2f18def198c8febc942f48957fa59.png",
  dermatologist: "https://static.prod-images.emergentagent.com/jobs/140b3304-82f5-413a-a4b6-594d9907228e/images/9577f6c0bf385c3e8629c76713a869cd46cc3c9bd783d620e50db50f3ad50595.png",
};

function AnimateOnScroll({ children, className = "", delay = 0 }) {
  const ref = useRef(null);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const observer = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting) {
        setTimeout(() => { el.classList.add("opacity-100", "translate-y-0"); el.classList.remove("opacity-0", "translate-y-8"); }, delay);
      }
    }, { threshold: 0.15 });
    observer.observe(el);
    return () => observer.disconnect();
  }, [delay]);
  return <div ref={ref} className={`opacity-0 translate-y-8 transition-all duration-700 ease-out ${className}`}>{children}</div>;
}

export default function PreventionTechPage() {
  return (
    <div className="min-h-screen bg-[#050510] text-white overflow-x-hidden" data-testid="prevention-tech-page">

      {/* =================== HERO =================== */}
      <section className="relative min-h-screen flex items-center justify-center">
        <div className="absolute inset-0">
          <img src={IMAGES.hero} alt="" className="w-full h-full object-cover opacity-30" />
          <div className="absolute inset-0 bg-gradient-to-b from-[#050510]/60 via-[#050510]/40 to-[#050510]" />
        </div>
        <div className="relative z-10 max-w-5xl mx-auto px-6 text-center">
          <div className="inline-flex items-center gap-2 px-4 py-1.5 bg-teal-500/10 border border-teal-500/20 rounded-full mb-8">
            <Shield className="w-4 h-4 text-teal-400" />
            <span className="text-sm text-teal-300 font-medium tracking-wide">ODR Academy Innovation Lab</span>
          </div>
          <h1 className="text-4xl sm:text-5xl lg:text-7xl font-bold leading-tight mb-6">
            <span className="text-white">La Tecnologia della</span><br />
            <span className="bg-gradient-to-r from-teal-400 via-emerald-400 to-cyan-400 bg-clip-text text-transparent">Prevenzione</span>
          </h1>
          <p className="text-lg sm:text-xl text-gray-300 max-w-3xl mx-auto mb-10 leading-relaxed">
            Quando l'estetica incontra l'intelligenza artificiale per salvare vite.
            Un sistema rivoluzionario che trasforma ogni trattamento estetico in un'opportunita di prevenzione dermatologica.
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <a href="#problema" className="px-8 py-3.5 bg-gradient-to-r from-teal-500 to-emerald-500 rounded-xl text-white font-semibold text-base hover:shadow-lg hover:shadow-teal-500/20 transition-all">
              Scopri il Progetto
            </a>
            <a href="#come-funziona" className="px-8 py-3.5 border border-gray-600 rounded-xl text-gray-300 font-medium hover:border-teal-500/50 hover:text-white transition-all">
              Come Funziona
            </a>
          </div>
        </div>
        <a href="#problema" className="absolute bottom-8 left-1/2 -translate-x-1/2 animate-bounce">
          <ChevronDown className="w-8 h-8 text-gray-500" />
        </a>
      </section>

      {/* =================== IL PROBLEMA =================== */}
      <section id="problema" className="py-24 px-6">
        <div className="max-w-5xl mx-auto">
          <AnimateOnScroll>
            <div className="text-center mb-16">
              <span className="text-xs uppercase tracking-[0.3em] text-red-400/80 font-semibold">Il Problema</span>
              <h2 className="text-3xl sm:text-4xl font-bold mt-4 mb-6">
                Ogni anno, <span className="text-red-400">migliaia di anomalie cutanee</span> passano inosservate
              </h2>
              <p className="text-gray-400 max-w-2xl mx-auto text-base leading-relaxed">
                Il melanoma e il tumore della pelle sono tra le neoplasie piu diffuse al mondo.
                La diagnosi precoce aumenta il tasso di sopravvivenza al <strong className="text-white">95%</strong>.
                Eppure, la maggior parte delle persone non si sottopone a controlli regolari.
              </p>
            </div>
          </AnimateOnScroll>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {[
              { num: "200.000+", label: "nuovi casi di melanoma all'anno in Europa", color: "text-red-400" },
              { num: "95%", label: "tasso di sopravvivenza con diagnosi precoce", color: "text-emerald-400" },
              { num: "85%", label: "delle donne frequenta regolarmente un centro estetico", color: "text-teal-400" }
            ].map((stat, i) => (
              <AnimateOnScroll key={i} delay={i * 150}>
                <div className="bg-[#0a0a1e] border border-gray-800/50 rounded-2xl p-8 text-center hover:border-gray-700/50 transition-all">
                  <p className={`text-4xl font-bold ${stat.color} mb-3`}>{stat.num}</p>
                  <p className="text-gray-400 text-sm">{stat.label}</p>
                </div>
              </AnimateOnScroll>
            ))}
          </div>
        </div>
      </section>

      {/* =================== LA VISIONE =================== */}
      <section className="py-24 px-6 bg-gradient-to-b from-[#050510] via-[#0a0f1a] to-[#050510]">
        <div className="max-w-5xl mx-auto">
          <AnimateOnScroll>
            <div className="text-center mb-16">
              <span className="text-xs uppercase tracking-[0.3em] text-teal-400/80 font-semibold">La Visione</span>
              <h2 className="text-3xl sm:text-4xl font-bold mt-4 mb-6">
                L'estetista come <span className="text-teal-400">sentinella della salute</span>
              </h2>
              <p className="text-gray-400 max-w-2xl mx-auto text-base leading-relaxed">
                Le estetiste, i parrucchieri e i fisioterapisti vedono la pelle dei propri clienti con regolarita, spesso piu dei medici stessi.
                Questo li rende le figure ideali per individuare cambiamenti sospetti — se dotati degli strumenti giusti.
              </p>
            </div>
          </AnimateOnScroll>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-center">
            <AnimateOnScroll>
              <img src={IMAGES.esthetician} alt="Estetista durante trattamento" className="rounded-2xl border border-gray-800/50 shadow-2xl shadow-teal-500/5 w-full" />
            </AnimateOnScroll>
            <AnimateOnScroll delay={200}>
              <div className="space-y-6">
                {[
                  { icon: Eye, title: "Osservazione Professionale", desc: "Estetiste, parrucchieri e fisioterapisti hanno accesso diretto alla pelle del cliente. Oltre 195.000 professionisti che possono fare la differenza." },
                  { icon: Heart, title: "Rapporto di Fiducia", desc: "La cliente si affida a questi professionisti. Questo rapporto facilita la comunicazione di eventuali anomalie senza creare panico." },
                  { icon: Shield, title: "Prevenzione Attiva", desc: "Non si tratta di fare diagnosi, ma di segnalare tempestivamente. 25.000 centri estetici, 100.000 acconciatori e 70.000 fisioterapisti: una rete capillare di prevenzione." }
                ].map((item, i) => {
                  const Icon = item.icon;
                  return (
                    <div key={i} className="flex gap-4">
                      <div className="w-12 h-12 rounded-xl bg-teal-500/10 border border-teal-500/20 flex items-center justify-center shrink-0">
                        <Icon className="w-5 h-5 text-teal-400" />
                      </div>
                      <div>
                        <h3 className="font-semibold text-white mb-1">{item.title}</h3>
                        <p className="text-gray-400 text-sm leading-relaxed">{item.desc}</p>
                      </div>
                    </div>
                  );
                })}
              </div>
            </AnimateOnScroll>
          </div>
        </div>
      </section>

      {/* =================== COME FUNZIONA =================== */}
      <section id="come-funziona" className="py-24 px-6">
        <div className="max-w-5xl mx-auto">
          <AnimateOnScroll>
            <div className="text-center mb-16">
              <span className="text-xs uppercase tracking-[0.3em] text-emerald-400/80 font-semibold">Come Funziona</span>
              <h2 className="text-3xl sm:text-4xl font-bold mt-4 mb-6">
                Dalla <span className="text-emerald-400">foto al dermatologo</span> in pochi secondi
              </h2>
            </div>
          </AnimateOnScroll>

          <div className="relative">
            {/* Connection line */}
            <div className="hidden lg:block absolute top-1/2 left-0 right-0 h-0.5 bg-gradient-to-r from-teal-500/0 via-teal-500/30 to-teal-500/0 -translate-y-1/2" />

            <div className="grid grid-cols-1 lg:grid-cols-4 gap-8">
              {[
                { step: "01", icon: Eye, title: "Individuazione", desc: "Durante il trattamento, l'estetista nota un neo sospetto, una macchia anomala o un cambiamento cutaneo.", color: "from-teal-500 to-teal-600" },
                { step: "02", icon: Smartphone, title: "Scatto & Invio", desc: "Con l'app sul proprio smartphone, scatta una foto ad alta risoluzione della zona interessata.", color: "from-emerald-500 to-emerald-600" },
                { step: "03", icon: Brain, title: "Analisi AI", desc: "L'intelligenza artificiale analizza l'immagine, classifica il livello di rischio e genera un report preliminare.", color: "from-cyan-500 to-cyan-600" },
                { step: "04", icon: UserCheck, title: "Dermatologo", desc: "L'immagine e il report vengono inviati al dermatologo di fiducia, che decide se convocare la cliente.", color: "from-blue-500 to-blue-600" }
              ].map((item, i) => {
                const Icon = item.icon;
                return (
                  <AnimateOnScroll key={i} delay={i * 150}>
                    <div className="relative bg-[#0a0a1e] border border-gray-800/50 rounded-2xl p-6 hover:border-gray-700/50 transition-all group">
                      <div className={`w-14 h-14 rounded-xl bg-gradient-to-br ${item.color} flex items-center justify-center mb-5 group-hover:scale-110 transition-transform`}>
                        <Icon className="w-6 h-6 text-white" />
                      </div>
                      <span className="text-[10px] uppercase tracking-[0.2em] text-gray-600 font-semibold">Step {item.step}</span>
                      <h3 className="text-lg font-bold text-white mt-1 mb-2">{item.title}</h3>
                      <p className="text-gray-400 text-sm leading-relaxed">{item.desc}</p>
                      {i < 3 && <ArrowRight className="hidden lg:block absolute -right-5 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-700" />}
                    </div>
                  </AnimateOnScroll>
                );
              })}
            </div>
          </div>
        </div>
      </section>

      {/* =================== TECNOLOGIA =================== */}
      <section className="py-24 px-6 bg-gradient-to-b from-[#050510] via-[#0a0f1a] to-[#050510]">
        <div className="max-w-5xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <AnimateOnScroll>
              <div>
                <span className="text-xs uppercase tracking-[0.3em] text-cyan-400/80 font-semibold">La Tecnologia</span>
                <h2 className="text-3xl sm:text-4xl font-bold mt-4 mb-6">
                  AI Vision di <span className="text-cyan-400">ultima generazione</span>
                </h2>
                <p className="text-gray-400 mb-8 leading-relaxed">
                  Il cuore del sistema e un modello di intelligenza artificiale addestrato su milioni di immagini dermatologiche,
                  capace di riconoscere pattern sospetti con una precisione superiore al 90%.
                </p>
                <div className="space-y-4">
                  {[
                    { label: "Analisi in tempo reale", desc: "Risultati in meno di 10 secondi" },
                    { label: "Classificazione del rischio", desc: "Basso / Medio / Alto con spiegazione" },
                    { label: "Report automatico", desc: "Documento professionale per il dermatologo" },
                    { label: "Storico paziente", desc: "Confronto nel tempo per monitorare l'evoluzione" },
                    { label: "Privacy by design", desc: "Dati crittografati, GDPR compliant" }
                  ].map((item, i) => (
                    <div key={i} className="flex items-start gap-3">
                      <CheckCircle className="w-5 h-5 text-cyan-400 mt-0.5 shrink-0" />
                      <div>
                        <span className="text-white font-medium text-sm">{item.label}</span>
                        <span className="text-gray-500 text-sm ml-1">— {item.desc}</span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </AnimateOnScroll>
            <AnimateOnScroll delay={200}>
              <img src={IMAGES.scan} alt="AI Scan" className="rounded-2xl border border-gray-800/50 shadow-2xl shadow-cyan-500/5 w-full" />
            </AnimateOnScroll>
          </div>
        </div>
      </section>

      {/* =================== FLUSSO DERMATOLOGO =================== */}
      <section className="py-24 px-6">
        <div className="max-w-5xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <AnimateOnScroll>
              <img src={IMAGES.dermatologist} alt="Dermatologo" className="rounded-2xl border border-gray-800/50 shadow-2xl shadow-blue-500/5 w-full" />
            </AnimateOnScroll>
            <AnimateOnScroll delay={200}>
              <div>
                <span className="text-xs uppercase tracking-[0.3em] text-blue-400/80 font-semibold">Il Dermatologo</span>
                <h2 className="text-3xl sm:text-4xl font-bold mt-4 mb-6">
                  Decisione <span className="text-blue-400">medica informata</span>
                </h2>
                <p className="text-gray-400 mb-8 leading-relaxed">
                  Il dermatologo riceve l'immagine ad alta risoluzione, il report AI e lo storico del paziente.
                  Ha tutti gli elementi per decidere se:
                </p>
                <div className="space-y-4">
                  {[
                    { color: "bg-green-500", label: "Monitoraggio", desc: "L'anomalia non e preoccupante — si monitora nel tempo" },
                    { color: "bg-amber-500", label: "Visita", desc: "Richiede un approfondimento — convocazione per visita" },
                    { color: "bg-red-500", label: "Urgente", desc: "Sospetto elevato — visita immediata e possibile biopsia" }
                  ].map((item, i) => (
                    <div key={i} className="flex items-start gap-4 bg-[#0a0a1e] border border-gray-800/50 rounded-xl p-4">
                      <div className={`w-3 h-3 rounded-full ${item.color} mt-1.5 shrink-0`} />
                      <div>
                        <span className="text-white font-semibold text-sm">{item.label}</span>
                        <p className="text-gray-400 text-sm mt-0.5">{item.desc}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </AnimateOnScroll>
          </div>
        </div>
      </section>

      {/* =================== BENEFICI =================== */}
      <section className="py-24 px-6 bg-gradient-to-b from-[#050510] via-[#0a0f1a] to-[#050510]">
        <div className="max-w-5xl mx-auto">
          <AnimateOnScroll>
            <div className="text-center mb-16">
              <span className="text-xs uppercase tracking-[0.3em] text-amber-400/80 font-semibold">I Benefici</span>
              <h2 className="text-3xl sm:text-4xl font-bold mt-4 mb-6">
                Un ecosistema che <span className="text-amber-400">beneficia tutti</span>
              </h2>
            </div>
          </AnimateOnScroll>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {[
              {
                title: "Per l'Estetista",
                icon: Sparkles,
                color: "from-teal-500 to-emerald-500",
                benefits: [
                  "Differenziazione professionale",
                  "Valore aggiunto al servizio",
                  "Fidelizzazione clienti",
                  "Ruolo sociale nella prevenzione",
                  "Formazione continua certificata"
                ]
              },
              {
                title: "Per l'Acconciatore",
                icon: Users,
                color: "from-fuchsia-500 to-purple-500",
                benefits: [
                  "Screening cuoio capelluto e nuca",
                  "100.000 professionisti coinvolti",
                  "Contatto frequente con il cliente",
                  "Individuazione neo sospetti in zone nascoste",
                  "Servizio innovativo e differenziante"
                ]
              },
              {
                title: "Per il Fisioterapista",
                icon: Activity,
                color: "from-cyan-500 to-blue-500",
                benefits: [
                  "70.000 professionisti attivi (59% donne)",
                  "Contatto diretto con la pelle del paziente",
                  "118 fisioterapisti ogni 100.000 abitanti",
                  "42% ha meno di 40 anni: generazione digitale",
                  "Diagnosi osservazionale durante le sedute"
                ]
              },
              {
                title: "Per la Cliente",
                icon: Heart,
                color: "from-pink-500 to-rose-500",
                benefits: [
                  "Screening durante trattamenti abituali",
                  "Nessun costo aggiuntivo",
                  "Accesso rapido al dermatologo",
                  "Monitoraggio nel tempo",
                  "Tranquillita e prevenzione"
                ]
              },
              {
                title: "Per il Dermatologo",
                icon: UserCheck,
                color: "from-blue-500 to-indigo-500",
                benefits: [
                  "Pre-screening AI affidabile",
                  "Immagini ad alta qualita",
                  "Report strutturato",
                  "Storico paziente completo",
                  "Rete capillare di segnalazione"
                ]
              }
            ].map((card, i) => {
              const Icon = card.icon;
              return (
                <AnimateOnScroll key={i} delay={i * 150}>
                  <div className="bg-[#0a0a1e] border border-gray-800/50 rounded-2xl p-8 hover:border-gray-700/50 transition-all h-full">
                    <div className={`w-14 h-14 rounded-xl bg-gradient-to-br ${card.color} flex items-center justify-center mb-5`}>
                      <Icon className="w-6 h-6 text-white" />
                    </div>
                    <h3 className="text-xl font-bold text-white mb-4">{card.title}</h3>
                    <ul className="space-y-2.5">
                      {card.benefits.map((b, j) => (
                        <li key={j} className="flex items-start gap-2 text-gray-400 text-sm">
                          <CheckCircle className="w-4 h-4 text-gray-600 mt-0.5 shrink-0" />
                          {b}
                        </li>
                      ))}
                    </ul>
                  </div>
                </AnimateOnScroll>
              );
            })}
          </div>
        </div>
      </section>

      {/* =================== NUMERI & IMPATTO =================== */}
      <section className="py-24 px-6">
        <div className="max-w-5xl mx-auto">
          <AnimateOnScroll>
            <div className="text-center mb-16">
              <span className="text-xs uppercase tracking-[0.3em] text-emerald-400/80 font-semibold">Impatto Potenziale</span>
              <h2 className="text-3xl sm:text-4xl font-bold mt-4">
                I numeri parlano <span className="text-emerald-400">chiaro</span>
              </h2>
            </div>
          </AnimateOnScroll>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-6">
            {[
              { icon: Users, num: "25.000+", label: "Centri estetici in Italia", color: "text-teal-400" },
              { icon: Clock, num: "100.000", label: "Acconciatori in Italia", color: "text-fuchsia-400" },
              { icon: Zap, num: "70.000+", label: "Fisioterapisti attivi in Italia", color: "text-cyan-400" },
              { icon: Heart, num: "95%", label: "Sopravvivenza con diagnosi precoce", color: "text-emerald-400" }
            ].map((item, i) => {
              const Icon = item.icon;
              return (
                <AnimateOnScroll key={i} delay={i * 100}>
                  <div className="bg-[#0a0a1e] border border-gray-800/50 rounded-2xl p-6 text-center hover:border-gray-700/50 transition-all">
                    <Icon className={`w-8 h-8 ${item.color} mx-auto mb-3`} />
                    <p className={`text-3xl font-bold ${item.color} mb-1`}>{item.num}</p>
                    <p className="text-gray-500 text-xs">{item.label}</p>
                  </div>
                </AnimateOnScroll>
              );
            })}
          </div>
        </div>
      </section>

      {/* =================== IMPATTO ECONOMICO (MODELLO PRUDENZIALE) =================== */}
      <section className="py-24 px-6 bg-gradient-to-b from-[#050510] via-[#0a0f1a] to-[#050510]">
        <div className="max-w-5xl mx-auto">
          <AnimateOnScroll>
            <div className="text-center mb-16">
              <span className="text-xs uppercase tracking-[0.3em] text-green-400/80 font-semibold">Impatto Economico</span>
              <h2 className="text-3xl sm:text-4xl font-bold mt-4 mb-6">
                Un modello <span className="text-green-400">economicamente sostenibile</span>
              </h2>
              <p className="text-gray-400 max-w-2xl mx-auto text-base">
                La letteratura scientifica dimostra che la diagnosi precoce genera risparmi significativi per il SSN.
                Le stime seguenti si basano su dati pubblicati e ipotesi prudenziali.
              </p>
            </div>
          </AnimateOnScroll>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-10">
            <AnimateOnScroll>
              <div className="bg-[#0a0a1e] border border-gray-800/50 rounded-2xl p-8">
                <p className="text-xs text-red-400 font-semibold uppercase tracking-wider mb-4">Range costo diagnosi tardiva</p>
                <p className="text-4xl font-bold text-red-400 mb-2">50.000 - 70.000 EUR</p>
                <p className="text-gray-400 text-sm">per paziente (stima da letteratura: terapie sistemiche, immunoterapia, ospedalizzazioni)</p>
                <a href="https://pmc.ncbi.nlm.nih.gov/articles/PMC10873772/" target="_blank" rel="noopener noreferrer"
                  className="text-xs text-blue-400 hover:underline mt-3 block">Rif. letteratura: PubMed Central, 2024</a>
              </div>
            </AnimateOnScroll>
            <AnimateOnScroll delay={150}>
              <div className="bg-[#0a0a1e] border border-gray-800/50 rounded-2xl p-8">
                <p className="text-xs text-green-400 font-semibold uppercase tracking-wider mb-4">Range costo diagnosi precoce</p>
                <p className="text-4xl font-bold text-green-400 mb-2">2.000 - 5.000 EUR</p>
                <p className="text-gray-400 text-sm">per paziente (chirurgia ambulatoriale curativa, guarigione elevata)</p>
                <a href="https://pmc.ncbi.nlm.nih.gov/articles/PMC10873772/" target="_blank" rel="noopener noreferrer"
                  className="text-xs text-blue-400 hover:underline mt-3 block">Rif. letteratura: PubMed Central, 2024</a>
              </div>
            </AnimateOnScroll>
          </div>

          <AnimateOnScroll>
            <div className="bg-gradient-to-r from-green-900/20 to-emerald-900/20 border border-green-500/20 rounded-2xl p-8 mb-8">
              <p className="text-xl font-bold text-green-400 mb-3 text-center">
                Anche con ipotesi prudenziali, la rete di oltre 190.000 professionisti puo contribuire
                all'individuazione precoce di migliaia di patologie cutanee
              </p>
              <p className="text-gray-300 text-center text-sm mb-6">
                Riducendo significativamente i costi sanitari e generando un risparmio potenziale
                superiore ai <strong className="text-white">60 milioni di euro annui</strong> per il SSN.
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                {[
                  { num: "Impatto scalabile", desc: "Maggiore e la rete attivata, maggiore il risparmio per il sistema sanitario", link: "https://www.sanitainformazione.it/immunoncologia-quattro-mila-morti-evitate-in-11-anni-il-ssn-ha-risparmiato-120-mln/" },
                  { num: "4.000 vite", desc: "morti evitate in Italia in 11 anni grazie alla diagnosi precoce (dato storico)", link: "https://www.sanitainformazione.it/immunoncologia-quattro-mila-morti-evitate-in-11-anni-il-ssn-ha-risparmiato-120-mln/" },
                  { num: "Modello virtuoso", desc: "Per ogni euro investito nello screening, il ritorno stimato supera i 3 euro", link: "https://pmc.ncbi.nlm.nih.gov/articles/PMC11745940/" }
                ].map((item, i) => (
                  <div key={i} className="text-center">
                    <p className="text-xl font-bold text-white mb-1">{item.num}</p>
                    <p className="text-gray-500 text-xs mb-1">{item.desc}</p>
                    <a href={item.link} target="_blank" rel="noopener noreferrer" className="text-[10px] text-blue-400 hover:underline">Rif. scientifico</a>
                  </div>
                ))}
              </div>
            </div>
          </AnimateOnScroll>

          <AnimateOnScroll>
            <div className="bg-[#0a0a1e] border border-gray-800/50 rounded-2xl p-8">
              <p className="text-xs text-amber-400 font-semibold uppercase tracking-wider mb-4">Evidenze dalla letteratura scientifica</p>
              <p className="text-gray-300 text-sm leading-relaxed mb-3">
                Uno studio modellistico su 25 anni condotto nella Regione Veneto ha dimostrato che un programma di screening
                risulta <strong className="text-white">dominante rispetto alla cura standard</strong>:
                costi inferiori e aspettativa di vita superiore.
              </p>
              <p className="text-gray-300 text-sm leading-relaxed mb-3">
                La microscopia confocale a riflettanza (RCM) ha evidenziato un <strong className="text-white">rapporto costi-benefici stimato di 3,89</strong>:
                per ogni euro investito nello screening, un ritorno di circa 3,89 euro in costi evitati.
              </p>
              <p className="text-gray-500 text-xs italic mt-3 mb-4">
                Nota: le stime economiche sono basate su modelli prudenziali e dati pubblicati su riviste peer-reviewed.
                I risultati effettivi possono variare in base al contesto di implementazione.
              </p>
              <div className="flex flex-wrap gap-3">
                <a href="https://pubmed.ncbi.nlm.nih.gov/31711359/" target="_blank" rel="noopener noreferrer"
                  className="text-xs text-blue-400 border border-blue-400/30 rounded-lg px-3 py-1.5 hover:bg-blue-400/10">Studio Veneto (PubMed)</a>
                <a href="https://pmc.ncbi.nlm.nih.gov/articles/PMC11745940/" target="_blank" rel="noopener noreferrer"
                  className="text-xs text-blue-400 border border-blue-400/30 rounded-lg px-3 py-1.5 hover:bg-blue-400/10">Studio RCM (PMC)</a>
                <a href="https://www.frontiersin.org/journals/public-health/articles/10.3389/fpubh.2025.1668198/full" target="_blank" rel="noopener noreferrer"
                  className="text-xs text-blue-400 border border-blue-400/30 rounded-lg px-3 py-1.5 hover:bg-blue-400/10">Frontiers in Public Health</a>
              </div>
            </div>
          </AnimateOnScroll>
        </div>
      </section>

      {/* =================== CTA =================== */}
      <section className="py-24 px-6">
        <div className="max-w-3xl mx-auto">
          <AnimateOnScroll>
            <div className="relative bg-gradient-to-br from-teal-900/30 to-emerald-900/30 border border-teal-500/20 rounded-3xl p-12 text-center overflow-hidden">
              <div className="absolute top-0 right-0 w-64 h-64 bg-teal-500/5 rounded-full blur-3xl" />
              <div className="absolute bottom-0 left-0 w-48 h-48 bg-emerald-500/5 rounded-full blur-3xl" />
              <div className="relative">
                <Shield className="w-12 h-12 text-teal-400 mx-auto mb-6" />
                <h2 className="text-3xl sm:text-4xl font-bold mb-4">
                  Insieme per la <span className="text-teal-400">prevenzione</span>
                </h2>
                <p className="text-gray-400 mb-8 max-w-xl mx-auto leading-relaxed">
                  Ogni centro estetico puo diventare un presidio di salute.
                  Ogni estetista puo fare la differenza.
                  La tecnologia e pronta. Il futuro della prevenzione inizia oggi.
                </p>
                <p className="text-lg font-semibold text-teal-300 mb-1">Ita-318 S.r.l. by ODR</p>
                <p className="text-sm text-gray-400">La Scienza del Benessere</p>
                <p className="text-xs text-gray-500 mt-2">CEO: Aldo Proia | Galleria Enzo Tortora 21, 10121 Torino</p>
                <p className="text-xs text-gray-500">Tel: +39 329 197 6870 | www.ita318.it</p>
              </div>
            </div>
          </AnimateOnScroll>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-8 px-6 border-t border-gray-900">
        <div className="max-w-5xl mx-auto text-center">
          <p className="text-gray-600 text-xs">
            &copy; {new Date().getFullYear()} Ita-318 S.r.l. by ODR &mdash; La Scienza del Benessere &mdash; Progetto Tecnologia della Prevenzione
          </p>
        </div>
      </footer>
    </div>
  );
}
